import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { sortBy } from 'lodash';
import { useQuery } from '@tanstack/react-query';

import {
    SCORECARD_REPORT,
    ADMIN_SCORE_CARD_LISTING_COLUMNS,
    QUERY_KEYS,
    DATE_TIME_FORMATS,
} from '../../../constants';
import { usePagination } from '../../../hooks';
import { AdminScoreCardHeader } from './AdminScoreCardHeader';
import { getAllPlatforms, popup, getAllScoreCardDetails, downloadCSV } from '../../../utils';
import { ResponsiveButton, Table } from '../../shared';
import { AdminScoreCardCreateModal } from './AdminScoreCardCreateModal';
import { useDebouncedValue } from '../../../hooks/useDebounceValue';
import { CSVIcon } from '../../../assets/svg';
import moment from 'moment';
import { scorecardCsvHeaderMap } from './ScoreCardsFunctions';

export const AdminScoreCardListing = () => {
    const searchParam = useSelector((s) => s.search.query);
    const [grade, setGrade] = useState('All grades');
    const [search, setSearch] = useState(searchParam);
    const [selectedPlatform, setSelectedPlatform] = useState({ id: '', name: 'All Platforms' });
    const [scorecards, setScorecards] = useState({ data: [], count: 0 });
    const pagination = usePagination(scorecards.data.length, scorecards.count);
    const [openCreateModal, setOpenCreateModal] = useState(false);
    const availableGradeOptions = ['All grades', 'red', 'yellow', 'green'];
    const csmCaseLoad = useSelector((s) => s.csmCaseload?.csmCaseloadData);

    const platformsQuery = useQuery({
        queryKey: [QUERY_KEYS.PLATFORMS],
        queryFn: async () => await getAllPlatforms({}),
        select: (data) => {
            const newPlatforms = [{ id: '', name: 'All Platforms' }, ...sortBy(data.data, 'name')];
            return newPlatforms;
        },
        placeholderData: [{ id: '', name: 'All Platforms' }],
    });

    const handleAddClick = () => {
        setOpenCreateModal(true);
    };

    const [debouncedSearch] = useDebouncedValue(search, 500);
    const { data, isFetched, isError, error, isFetching } = useQuery({
        queryKey: [
            QUERY_KEYS.SCORECARD_LIST,
            pagination.offset,
            pagination.limit,
            debouncedSearch,
            grade,
            selectedPlatform,
            csmCaseLoad?.id,
        ],
        queryFn: () =>
            getAllScoreCardDetails({
                offset: pagination.offset,
                limit: pagination.limit,
                search: search ? debouncedSearch : '',
                selectedPlatform: selectedPlatform ? selectedPlatform : {},
                list: true,
                grade: grade && grade !== 'All grades' ? grade : '',
                id: csmCaseLoad?.id || '',
                role: csmCaseLoad?.role_code || '',
            }),
        select: (data) => ({
            data: data?.data || [],
            count: data?.count || 0,
        }),
        placeholderData: { data: [], count: 0 },
    });
    if (isError) popup('error', error.message);

    useEffect(() => {
        if (data && isFetched) {
            setScorecards(data);
        }
    }, [data, isFetched]);

    const allScorecardsQuery = useQuery({
        queryKey: [QUERY_KEYS.ALL_SCORECARD_LIST],
        queryFn: () => getAllScoreCardDetails({ list: true }),
        enabled: false,
    });

    const onCSVExportClick = async () => {
        const { data } = await allScorecardsQuery.refetch();
        downloadCSV({
            data: data.data,
            headersMap: scorecardCsvHeaderMap,
            filename: `scorecards_report_${moment().format(DATE_TIME_FORMATS.DATE_TIME_UNDERSCORE)}`,
        });
    };

    return (
        <>
            <AdminScoreCardHeader
                platforms={platformsQuery?.data}
                availableGrades={availableGradeOptions}
                setSelectedPlatform={setSelectedPlatform}
                selectedPlatform={selectedPlatform}
                setSearch={setSearch}
                grade={grade}
                setGrade={setGrade}
                handleAddClick={handleAddClick}
                heading={SCORECARD_REPORT.SCORE_CARD_LISTING_PAGE.HEADER_HEADING}
                buttonText={SCORECARD_REPORT.SCORE_CARD_LISTING_PAGE.HEADER_BUTTON}
                searchBarPlaceholder={
                    SCORECARD_REPORT.SCORE_CARD_LISTING_PAGE.SEARCH_BAR_PLACEHOLDER
                }
                search={search}
                additionalElement={
                    <ResponsiveButton
                        title='Export Scorecards as CSV'
                        onClick={onCSVExportClick}
                        disabled={allScorecardsQuery.isFetching}
                        breakpoint={575}
                        icon={<CSVIcon currentColor />}
                        leftIcon={<CSVIcon currentColor />}
                        label={'Export as CSV'}
                        isLoading={allScorecardsQuery.isFetching}
                        isWhite
                    />
                }
            />

            {openCreateModal ? (
                <AdminScoreCardCreateModal
                    openCreateModal={openCreateModal}
                    setOpenCreateModal={setOpenCreateModal}
                />
            ) : (
                ''
            )}
            <Table
                columns={ADMIN_SCORE_CARD_LISTING_COLUMNS}
                data={scorecards.data}
                pagination={pagination}
                pageName={SCORECARD_REPORT.SCORE_CARD_LISTING_PAGE.HEADER_HEADING}
                stickyColumns={['Client']}
                isLoading={isFetching}
            />
        </>
    );
};
